import React from "react";

interface BotanicalIconProps {
  category: string;
  className?: string;
  variant?: "icon" | "illustration";
}

/**
 * Premium SVG Path Illustrators for Botanical/Plant Species
 * Styled in a high-contrast scientific/encyclopedia diagram look. 
 * Replaces photos completely using clean, high-performance vector graphics.
 */
export function BotanicalIcon({ category, className = "w-6 h-6", variant = "icon" }: BotanicalIconProps) {
  const normalizedCategory = (category || "").toLowerCase().trim();

  // Helper colors based on category
  const getColorClasses = () => {
    if (normalizedCategory.includes("lavender") || normalizedCategory.includes("purple")) {
      return { stroke: "stroke-purple-600", fill: "fill-purple-50", text: "text-purple-700", bg: "bg-purple-50/40" };
    }
    if (normalizedCategory.includes("tropical") || normalizedCategory.includes("exotic") || normalizedCategory.includes("bromeliad") || normalizedCategory.includes("jungle") || normalizedCategory.includes("monstera")) {
      return { stroke: "stroke-emerald-800", fill: "fill-emerald-100", text: "text-emerald-950", bg: "bg-emerald-50/60" };
    }
    if (normalizedCategory.includes("indoor") || normalizedCategory.includes("houseplant")) {
      return { stroke: "stroke-emerald-600", fill: "fill-emerald-50", text: "text-emerald-700", bg: "bg-emerald-50/40" };
    }
    if (normalizedCategory.includes("succulent") || normalizedCategory.includes("cactus")) {
      return { stroke: "stroke-teal-600", fill: "fill-teal-50", text: "text-teal-700", bg: "bg-teal-50/40" };
    }
    if (normalizedCategory.includes("tree")) {
      return { stroke: "stroke-emerald-700", fill: "fill-emerald-50", text: "text-emerald-800", bg: "bg-emerald-50/40" };
    }
    if (normalizedCategory.includes("herb") || normalizedCategory.includes("fruit") || normalizedCategory.includes("vegetable") || normalizedCategory.includes("culinary")) {
      return { stroke: "stroke-green-600", fill: "fill-green-50", text: "text-green-700", bg: "bg-green-50/40" };
    }
    if (normalizedCategory.includes("medicinal") || normalizedCategory.includes("medical")) {
      return { stroke: "stroke-indigo-600", fill: "fill-indigo-50", text: "text-indigo-700", bg: "bg-indigo-50/40" };
    }
    if (normalizedCategory.includes("flower") || normalizedCategory.includes("bloom")) {
      return { stroke: "stroke-rose-500", fill: "fill-rose-50", text: "text-rose-600", bg: "bg-rose-50/40" };
    }
    if (normalizedCategory.includes("shrub") || normalizedCategory.includes("bush")) {
      return { stroke: "stroke-lime-600", fill: "fill-lime-50", text: "text-lime-700", bg: "bg-lime-50/40" };
    }
    if (normalizedCategory.includes("climber") || normalizedCategory.includes("vine") || normalizedCategory.includes("climb")) {
      return { stroke: "stroke-green-750", fill: "fill-green-100", text: "text-green-800", bg: "bg-green-50/40" };
    }
    if (normalizedCategory.includes("fern")) {
      return { stroke: "stroke-emerald-500", fill: "fill-emerald-50", text: "text-emerald-600", bg: "bg-emerald-50/40" };
    }
    // Default
    return { stroke: "stroke-green-650", fill: "fill-green-50", text: "text-green-700", bg: "bg-green-50/30" };
  };

  const colors = getColorClasses();

  // 1. INDOOR PLANTS (Potted aesthetic houseplant with rich architectural leaves)
  const renderIndoor = () => (
    <svg viewBox="0 0 100 100" className={`${className} ${colors.stroke}`} fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      {/* Soil/Pot */}
      <path d="M35 70 L65 70 L60 90 L40 90 Z" className={colors.fill} strokeWidth="3" />
      <path d="M30 65 H70" strokeWidth="3.5" />
      {/* Left elegant split-leaf */}
      <path d="M32 65 C20 50 18 35 32 20 C36 26 38 34 32 65 Z" className={colors.fill} />
      <path d="M22 45 C30 45 32 45 32 45" />
      <path d="M26 33 C33 36 33 36 33 36" />
      {/* Right elegant split-leaf */}
      <path d="M68 65 C80 50 82 35 68 20 C64 26 62 34 68 65 Z" className={colors.fill} />
      <path d="M78 45 C70 45 68 45 68 45" />
      <path d="M74 33 C67 36 67 36 67 36" />
      {/* Center young sprout shoot */}
      <path d="M50 65 L50 15" strokeWidth="2" />
      <path d="M50 15 C42 22 45 40 50 65 C55 40 58 22 50 15 Z" className={colors.fill} />
    </svg>
  );

  // 2. SUCCULENTS / CACTI (Architectural desert ribbed cactus with spines)
  const renderSucculent = () => (
    <svg viewBox="0 0 100 100" className={`${className} ${colors.stroke}`} fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      {/* Terracotta bowl */}
      <path d="M25 75 C25 88 75 88 75 75 Z" className={colors.fill} strokeWidth="3" />
      <line x1="20" y1="75" x2="80" y2="75" strokeWidth="3.5" />
      {/* Center major cactus stem */}
      <path d="M42 75 V25 C42 18 58 18 58 25 V75" className={colors.fill} strokeWidth="2.8" />
      {/* Vertical rib lines */}
      <path d="M50 21 V75" d-type="rib" strokeDasharray="1 1" />
      <path d="M46 25 C47 40 47 60 46 75" strokeDasharray="2 2" />
      <path d="M54 25 C53 40 53 60 54 75" strokeDasharray="2 2" />
      {/* Left arm */}
      <path d="M42 55 H30 C24 55 24 40 24 40 V30" className={colors.fill} />
      {/* Right arm */}
      <path d="M58 48 H70 C76 48 76 35 76 35 V28" className={colors.fill} />
      {/* Little micro spines */}
      <line x1="38" y1="30" x2="33" y2="28" />
      <line x1="62" y1="35" x2="67" y2="33" />
      <line x1="18" y1="38" x2="23" y2="38" />
      <line x1="82" y1="32" x2="77" y2="32" />
    </svg>
  );

  // 3. TREES (Majestic trunk with ornamental cellular canopy rings)
  const renderTree = () => (
    <svg viewBox="0 0 100 100" className={`${className} ${colors.stroke}`} fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      {/* Tree Ground */}
      <path d="M20 90 H80" strokeWidth="3.2" />
      {/* Main trunks */}
      <path d="M44 90 V60 C44 55 40 50 36 45" strokeWidth="3.5" />
      <path d="M56 90 V55 C56 48 64 42 68 38" strokeWidth="3.5" />
      <path d="M50 90 V45" strokeWidth="4.2" />
      {/* Big overlapping concentric foliage layers */}
      <circle cx="50" cy="35" r="22" className={colors.fill} strokeWidth="2.5" />
      <circle cx="36" cy="45" r="16" className={colors.fill} strokeWidth="2" />
      <circle cx="64" cy="42" r="17" className={colors.fill} strokeWidth="2" />
      {/* Internal concentric scientific styling lines */}
      <circle cx="50" cy="35" r="12" strokeDasharray="3 3" />
      <circle cx="36" cy="45" r="8" strokeDasharray="2 1" />
    </svg>
  );

  // 4. HERBS (Symmetrical tasty culinary branch with detailed fine vein lines)
  const renderHerb = () => (
    <svg viewBox="0 0 100 100" className={`${className} ${colors.stroke}`} fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      {/* Stem */}
      <path d="M50 90 L50 15" strokeWidth="3" />
      {/* Top Leaf */}
      <path d="M50 15 C44 3 44 3 50 3 C56 3 56 3 50 15 Z" className={colors.fill} />
      {/* Left leaves cascade */}
      <path d="M50 35 C28 35 22 20 28 15 C34 10 44 22 50 35 Z" className={colors.fill} />
      <path d="M50 55 C20 58 12 40 20 32 C28 24 44 42 50 55 Z" className={colors.fill} />
      <path d="M50 75 C15 80 10 60 17 50 C24 40 44 60 50 75 Z" className={colors.fill} />
      
      {/* Right leaves cascade */}
      <path d="M50 35 C72 35 78 20 72 15 C66 10 56 22 50 35 Z" className={colors.fill} />
      <path d="M50 55 C80 58 88 40 80 32 C72 24 56 42 50 55 Z" className={colors.fill} />
      <path d="M50 75 C85 80 90 60 83 50 C76 40 56 60 50 75 Z" className={colors.fill} />

      {/* Scientific node marks */}
      <circle cx="50" cy="35" r="1.5" className="fill-green-600" />
      <circle cx="50" cy="55" r="1.5" className="fill-green-600" />
      <circle cx="50" cy="75" r="1.5" className="fill-green-600" />
    </svg>
  );

  // 5. MEDICINAL (Leaf combined with a health care cross or structured veins)
  const renderMedicinal = () => (
    <svg viewBox="0 0 100 100" className={`${className} ${colors.stroke}`} fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      {/* Clean circular border for laboratory vibe */}
      <circle cx="50" cy="50" r="45" strokeDasharray="4 2" className="stroke-indigo-300" strokeWidth="1" />
      {/* Main leaf */}
      <path d="M50 82 C25 65 20 35 50 15 C80 35 75 65 50 82 Z" className={colors.fill} strokeWidth="3" />
      <path d="M50 15 V82" strokeWidth="2" />
      {/* Medical/Aqueous Health cross over leaf */}
      <rect x="42" y="42" width="16" height="16" rx="3" className="fill-white" strokeWidth="1.5" />
      <path d="M50 45 V55 M45 50 H55" strokeWidth="3" className="stroke-indigo-600" />
      {/* Elegant medicinal branch veins */}
      <path d="M50 25 Q35 32 30 40" strokeWidth="1.5" />
      <path d="M50 25 Q65 32 70 40" strokeWidth="1.5" />
      <path d="M50 60 Q34 68 28 72" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M50 60 Q66 68 72 72" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );

  // 6. FLOWERING (Brilliant blooming wild orchid flower structure)
  const renderFlowering = () => (
    <svg viewBox="0 0 100 100" className={`${className} ${colors.stroke}`} fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      {/* Stalk */}
      <path d="M50 95 V65" strokeWidth="3" className="stroke-emerald-600" />
      {/* Symmetrical blooming petals */}
      <g transform="translate(50, 50)">
        {/* Rear offset petals */}
        <path d="M0 0 C-25 -25 -25 -55 0 -55 C25 -55 25 -25 0 0 Z" className={colors.fill} />
        <path d="M0 0 C-50 -10 -55 -30 -35 -45 C-15 -60 5 -15 0 0 Z" className={colors.fill} />
        <path d="M0 0 C50 -10 55 -30 35 -45 C15 -60 -5 -15 0 0 Z" className={colors.fill} />
        
        {/* Major left petal */}
        <path d="M0 0 C-45 10 -40 38 -20 38 C0 38 10 10 0 0 Z" className={colors.fill} strokeWidth="2" />
        {/* Major right petal */}
        <path d="M0 0 C45 10 40 38 20 38 C0 38 -10 10 0 0 Z" className={colors.fill} strokeWidth="2" />
        
        {/* Core Pistil/Anther detail */}
        <circle cx="0" cy="-5" r="7" className="fill-amber-400 stroke-amber-600" strokeWidth="1.5" />
        <circle cx="-3" cy="-12" r="2.5" className="fill-rose-600 stroke-rose-600" />
        <circle cx="3" cy="-12" r="2.5" className="fill-rose-600 stroke-rose-600" />
      </g>
    </svg>
  );

  // 7. TROPICAL (Split leaf palm/monstera giant leaf)
  const renderTropical = () => (
    <svg viewBox="0 0 100 100" className={`${className} ${colors.stroke}`} fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      {/* Massive Swiss leaf contour */}
      <path d="M50 90 C15 70 8 35 45 10 C50 6 50 6 55 10 C92 35 85 70 50 90 Z" className={colors.fill} strokeWidth="3" />
      {/* Central strong spine */}
      <path d="M50 10 V90" strokeWidth="3" />
      {/* Symmetrical deep leaf fenestrations / cutouts */}
      {/* Left incisions */}
      <path d="M48 30 H22" strokeWidth="3" />
      <path d="M48 45 H12" strokeWidth="3" />
      <path d="M48 60 H18" strokeWidth="3" />
      <path d="M48 72 H28" strokeWidth="3" />
      {/* Right incisions */}
      <path d="M52 30 H78" strokeWidth="3" />
      <path d="M52 45 H88" strokeWidth="3" />
      <path d="M52 60 H82" strokeWidth="3" />
      <path d="M52 72 H72" strokeWidth="3" />
      {/* Tiny scientific holes (fenestrations) list */}
      <circle cx="36" cy="38" r="3" className="fill-emerald-800" stroke="none" />
      <circle cx="64" cy="38" r="3" className="fill-emerald-800" stroke="none" />
      <circle cx="36" cy="53" r="4" className="fill-emerald-800" stroke="none" />
      <circle cx="64" cy="53" r="4" className="fill-emerald-800" stroke="none" />
    </svg>
  );

  // 8. SHRUBS (Hedge layer/bush clusters)
  const renderShrub = () => (
    <svg viewBox="0 0 100 100" className={`${className} ${colors.stroke}`} fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      {/* Ground support */}
      <path d="M15 88 H85" strokeWidth="3" />
      {/* Branch networks */}
      <path d="M30 88 C32 75 42 65 50 65" strokeWidth="2" />
      <path d="M70 88 C68 75 58 65 50 65" strokeWidth="2" />
      <path d="M50 88 V60" strokeWidth="2.5" />
      {/* Shrubby foliage clouds */}
      <path d="M35 78 C25 78 18 70 24 60 C18 50 25 38 35 42 C40 30 60 30 65 42 C75 38 82 50 76 60 C82 70 75 78 65 78 Z" className={colors.fill} strokeWidth="3" />
      {/* Tiny internal aesthetic leaves */}
      <path d="M42 50 C44 46 48 46 50 50" />
      <path d="M55 58 C53 62 50 62 48 58" />
      <path d="M34 60 C38 60 40 57 38 54" />
    </svg>
  );

  // 9. CLIMBERS / VINE (Twisted coil with climbing leaves)
  const renderVine = () => (
    <svg viewBox="0 0 100 100" className={`${className} ${colors.stroke}`} fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      {/* Twisted trellis framework */}
      <path d="M30 95 L70 5 M70 95 L30 5" strokeDasharray="2 3" className="stroke-gray-300" strokeWidth="1.5" />
      {/* S-curve core vine */}
      <path d="M50 95 Q30 75 50 50 T50 5" strokeWidth="3.5" />
      {/* Heart vine leaves at turning nodes */}
      <path d="M40 70 C30 70 25 55 40 45 C45 55 44 65 40 70 Z" className={colors.fill} />
      <path d="M60 35 C70 35 75 50 60 60 C55 50 56 40 60 35 Z" className={colors.fill} />
      <path d="M35 22 C25 22 20 10 35 3 C40 10 39 18 35 22 Z" className={colors.fill} />
      {/* Little vine curl tendril details */}
      <path d="M50 48 Q65 44 60 38" strokeWidth="1.5" />
      <path d="M48 76 Q30 80 32 86" strokeWidth="1.5" />
    </svg>
  );

  // 10. FERNS (Feathered pinnfrond)
  const renderFern = () => (
    <svg viewBox="0 0 100 100" className={`${className} ${colors.stroke}`} fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      {/* Arching stem rachis */}
      <path d="M20 90 Q50 60 85 10" strokeWidth="3.5" />
      {/* pinnules/leaflets protruding outwards symmetrical along rachis */}
      <path d="M48 64 Q32 75 30 70 C34 58 48 58 48 64 Z" className={colors.fill} />
      <path d="M52 61 Q68 70 70 65 C68 53 54 55 52 61 Z" className={colors.fill} />
      
      <path d="M57 49 Q42 58 40 53 C43 42 55 44 57 49 Z" className={colors.fill} />
      <path d="M61 46 Q76 53 78 48 C75 37 63 40 61 46 Z" className={colors.fill} />
      
      <path d="M66 34 Q53 41 51 36 C53 27 63 29 66 34 Z" className={colors.fill} />
      <path d="M69 31 Q82 36 84 31 C81 22 71 25 69 31 Z" className={colors.fill} />
      
      <path d="M74 20 Q63 25 61 21 C63 14 71 15 74 20 Z" className={colors.fill} />
      <path d="M76 18 Q85 21 86 16 C83 9 75 12 76 18 Z" className={colors.fill} />

      {/* Sprout tip coiled fiddlehead */}
      <path d="M85 10 Q88 7 88 5 C86 -1 81 2 82 5 Q82 11 81 10" strokeWidth="2" />
    </svg>
  );

  // 11. SNAKE PLANT (Sword-like, upright vertical leaves with beautiful cross variegation)
  const renderSnakePlant = () => (
    <svg viewBox="0 0 100 100" className={`${className} ${colors.stroke}`} fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      {/* Terracotta Pot */}
      <path d="M38 75 L62 75 L58 92 L42 92 Z" className={colors.fill} strokeWidth="2.8" />
      <path d="M34 70 H66" strokeWidth="3.2" />
      
      {/* Center Tall Leaf */}
      <path d="M50 70 C46 50 44 25 50 8 C56 25 54 50 50 70 Z" className={colors.fill} strokeWidth="2" />
      {/* Wave lines inside center leaf for variegation */}
      <path d="M47 55 Q50 53 53 55" strokeWidth="1.5" />
      <path d="M48 40 Q51 38 52 40" strokeWidth="1.5" />
      <path d="M49 25 Q51 23 51 25" strokeWidth="1.5" />

      {/* Left Medium Leaf (curved slightly left) */}
      <path d="M42 70 C33 50 32 30 38 18 C44 32 46 52 42 70 Z" className={colors.fill} strokeWidth="1.8" />
      <path d="M36 58 Q40 56 42 59" strokeWidth="1.5" />
      <path d="M35 44 Q39 42 41 45" strokeWidth="1.5" />
      <path d="M36 30 Q39 28 40 31" strokeWidth="1.5" />

      {/* Right Medium Leaf (curved slightly right) */}
      <path d="M58 70 C67 50 68 30 62 18 C56 32 54 52 58 70 Z" className={colors.fill} strokeWidth="1.8" />
      <path d="M58 59 Q60 56 64 58" strokeWidth="1.5" />
      <path d="M59 45 Q61 42 65 44" strokeWidth="1.5" />
      <path d="M60 31 Q61 28 64 30" strokeWidth="1.5" />

      {/* Small far-left outer leaf */}
      <path d="M38 70 C28 58 26 42 32 30 C35 42 39 56 38 70 Z" className={colors.fill} strokeWidth="1.5" />
      
      {/* Small far-right outer leaf */}
      <path d="M62 70 C72 58 74 42 68 30 C65 42 61 56 62 70 Z" className={colors.fill} strokeWidth="1.5" />
    </svg>
  );

  const getRenderFunction = () => {
    if (normalizedCategory.includes("snake") || normalizedCategory.includes("sansevieria")) {
      return renderSnakePlant;
    }
    if (normalizedCategory.includes("tropical") || normalizedCategory.includes("exotic") || normalizedCategory.includes("bromeliad") || normalizedCategory.includes("jungle") || normalizedCategory.includes("monstera")) {
      return renderTropical;
    }
    if (normalizedCategory.includes("indoor") || normalizedCategory.includes("houseplant")) {
      return renderIndoor;
    }
    if (normalizedCategory.includes("succulent") || normalizedCategory.includes("cactus") || normalizedCategory.includes("desert")) {
      return renderSucculent;
    }
    if (normalizedCategory.includes("tree")) {
      return renderTree;
    }
    if (normalizedCategory.includes("herb") || normalizedCategory.includes("fruit") || normalizedCategory.includes("vegetable") || normalizedCategory.includes("culinary")) {
      return renderHerb;
    }
    if (normalizedCategory.includes("medicinal") || normalizedCategory.includes("medical")) {
      return renderMedicinal;
    }
    if (normalizedCategory.includes("flower") || normalizedCategory.includes("bloom")) {
      return renderFlowering;
    }
    if (normalizedCategory.includes("shrub") || normalizedCategory.includes("bush")) {
      return renderShrub;
    }
    if (normalizedCategory.includes("climber") || normalizedCategory.includes("vine") || normalizedCategory.includes("climb")) {
      return renderVine;
    }
    if (normalizedCategory.includes("fern")) {
      return renderFern;
    }
    // General fallback
    return renderIndoor;
  };

  const renderComponent = getRenderFunction();

  if (variant === "illustration") {
    // Large, detailed high contrast blueprint / laboratory diagram backing card
    return (
      <div className={`w-full h-full ${colors.bg} rounded-3xl flex flex-col items-center justify-center p-8 select-none border border-green-100/50 relative overflow-hidden group/vector`}>
        {/* Subtle grid backdrop for technical look */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
          backgroundImage: `radial-gradient(#10b981 1px, transparent 1px)`,
          backgroundSize: "16px 16px"
        }} />
        
        {/* Secondary scientific coordinates lines */}
        <div className="absolute top-4 left-4 font-mono text-[8px] text-gray-400 font-bold uppercase tracking-wider">
          SPECIMEN NO: PL-{category.substring(0,3).toUpperCase()}
        </div>
        <div className="absolute bottom-4 right-4 font-mono text-[8.5px] text-gray-400 font-bold uppercase tracking-widest flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          SYSTEM: PHOTO-FREE SCIENTIFIC REGISTER
        </div>

        {/* Vector Illustrator Rendered Here */}
        <div className="w-36 h-36 relative z-10 drop-shadow-sm transition-transform duration-300 group-hover/vector:scale-105 group-hover/vector:rotate-1">
          {renderComponent()}
        </div>

        {/* Diagnostic Metadata Overlay */}
        <div className="mt-4 text-center z-10 space-y-1">
          <span className={`text-[9px] uppercase font-bold tracking-widest px-2.5 py-0.5 rounded-full ${colors.bg} ${colors.text} border border-green-200/20`}>
            {category} Taxonomy
          </span>
          <p className="text-[10px] text-gray-400 font-medium italic mt-1 font-mono">
            Vector index reference authenticated
          </p>
        </div>
      </div>
    );
  }

  return renderComponent();
}
